<template>
  <ion-page>
    <LargeNavbar
      show-back-button
      @back-button="onBackButton"
      :show-actions="false"
      :title="t('bookingDetails.myPage')"
    >
      <template #end>
        <ion-buttons v-if="booking" slot="end">
          <ion-menu-button
            :auto-hide="false"
            @click="
              router.push({
                name: 'Chat',
                params: { bookingId: booking.id },
                query: { user: 'owner' },
              })
            "
          >
            <ion-icon :icon="chatbox" />
          </ion-menu-button>
        </ion-buttons>
      </template>
    </LargeNavbar>
    <ion-content :fullscreen="true">
      <div class="blue-decorative"></div>
      <div v-if="!isLoading">
        <div v-if="!isActiveBooking">
          <RejectedBookingView :rental-unit="rentalUnit" :booking="booking" />
        </div>
        <div v-else>
          <BookingDetailsPageContent
            :rental-unit="rentalUnit"
            :booking="booking"
            :contract="contractData"
          />
        </div>
      </div>
      <div v-else>
        <div class="PageContainer mt-xs d-flex flex-column align-center w-100">
          <div class="content w-100 d-flex flex-column gap-xs">
            <SkeletonLoaderCard />
            <SkeletonLoaderCard />
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
  import router from '@/router';
  import { onMounted, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useI18n } from 'vue-i18n';
  import LargeNavbar from '@/components/LargeNavbar.vue';
  import { IonPage, IonContent, IonButtons, IonMenuButton, IonIcon } from '@ionic/vue';
  import { useBookingStore } from '@/stores/booking-store';
  import { useToast } from '@/composables/useToast';
  import { useElasticStore } from '@/stores/elastic-store';
  import SkeletonLoaderCard from '@/components/loaders/SkeletonLoaderCard.vue';
  import { useBooking } from '@/composables/useBooking';
  import { useRoute } from 'vue-router';
  import { BookingForm } from '@/interfaces/booking/booking.interface';
  import BookingDetailsPageContent from '@/components/my-page/BookingDetailsPageContent.vue';
  import RejectedBookingView from '@/components/booking/RejectedBookingView.vue';
  import { useCheckInStore } from '@/stores/checkin-store';
  import { useInvoices } from '@/composables/useInvoices';
  import { chatbox } from 'ionicons/icons';

  const { t } = useI18n();
  const isActiveBooking = ref(true);
  const { contractData } = storeToRefs(useBookingStore());
  const { getBooking } = useBookingStore();
  const { showToast } = useToast();
  const { handleGetReservationContract } = useBooking();
  const { getRentalUnitPreview } = useElasticStore();
  const { getChecks } = useCheckInStore();
  const { getInvoicesList } = useInvoices();

  const isLoading = ref(true);

  const rentalUnit = ref();
  const booking = ref();

  const route = useRoute();

  const onBackButton = () => {
    router.back();
  };

  const handleGetRentalPreview = async (id: string) => {
    try {
      const response = await getRentalUnitPreview(id);
      if (response.id) {
        rentalUnit.value = response;
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    }
  };

  const handleGetBooking = async (bookingId: string) => {
    try {
      isLoading.value = true;

      const response = await getBooking(bookingId);
      if (response) {
        booking.value = response;
        await handleGetRentalPreview(response.RentalUnit?.publicId as string);
        await handleGetReservationContract(bookingId);
        await checkBookingAction(response);

        if (response.status?.toString() !== 'pending') {
          await getChecksReport(bookingId);
        }
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      isLoading.value = false;
    }
  };

  const getChecksReport = async (bookingId: string) => {
    try {
      await getChecks(bookingId, 'check-in');
      await getChecks(bookingId, 'check-out');
    } catch (error) {
      console.error(error);
    }
  };

  const checkBookingAction = async (booking: BookingForm) => {
    if (booking.status === 'canceled') {
      isActiveBooking.value = false;
    }
  };

  const initialConfig = async () => {
    await handleGetBooking(route.params.bookingId as string);
    await getInvoicesList(1);
  };

  onMounted(async () => {
    initialConfig();
  });
</script>

<style lang="css" scoped></style>
